import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import Search from 'components/Search';
import useIsMobile from 'hooks/useIsMobile';
import useSelectionOrigin from 'hooks/useSelectionOrigin';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GroupedListModel } from 'models/groupedList';
import RuleContainer from 'screens/containers/Rule';
import { htmlT } from 'utils/generic';

import { StyledButton, ButtonContainer, ButtonGhost, BoxContainer, List, SubTitle } from './styles';

interface Props {
  showRulesSelection?: boolean;
  groupTarget?: string;
  ruleList: GroupedListModel[];
  isLoading: boolean;
  ruleSelected?: GroupedListModel;
  onCloseSelectionScreen: () => void;
  onConfirmSelection: (rule: GroupedListModel) => void;
}

const RuleSelection = ({
  groupTarget,
  ruleList,
  isLoading,
  onCloseSelectionScreen,
  onConfirmSelection,
  ruleSelected,
  showRulesSelection,
}: Props) => {
  const [showSearch, setShowSearch] = useState(false);
  const [rulesResult, setRulesResult] = useState<GroupedListModel[]>([]);
  const [selectedRule, setSelectedRule] = useState<GroupedListModel[]>([]);
  const [showRule, setShowRule] = useState(false);
  const history = useHistory();
  const { itemCreated, updateItemCreated } = useSelectionOrigin();
  const { t } = useStoreTranslation();
  const isMobile = useIsMobile();

  const ruleId = '';
  const searchResultsText = htmlT(t, 'group.groupAggregation.searchResults', { count: rulesResult?.length });

  const handleSelection = (rule: GroupedListModel) => {
    setSelectedRule([rule]);
  };

  const handleOnSearch = (value: string) => {
    const userListAfterSelected = ruleList.filter((user) => !selectedRule.find((selected) => user.id === selected.id));

    if (value) {
      setRulesResult(
        userListAfterSelected.filter(
          (name: GroupedListModel) => name.name && name.name.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    } else setRulesResult([]);
  };

  const handleSubmitSelection = () => {
    if (selectedRule.length > 0) onConfirmSelection(Object.assign({}, ...selectedRule));
    updateItemCreated(null);
    onCloseSelectionScreen();
  };

  const handleNavigateHistory = () => {
    if (showRulesSelection) {
      updateItemCreated(null);
      if (onCloseSelectionScreen) onCloseSelectionScreen();
    } else {
      history.push('/corporate/dashboard');
    }
  };

  const renderAddRule = () => (
    <ApplicationOverlayContent sidebar width={1000} show={showRule} onClose={() => setShowRule(false)}>
      <RuleContainer
        originGroup={groupTarget}
        isFromSelection={showRulesSelection}
        match={{ params: { ruleId } }}
        onCloseFormRule={() => setShowRule(false)}
      />
    </ApplicationOverlayContent>
  );

  useEffect(() => {
    if (itemCreated) {
      setSelectedRule(
        itemCreated.map((rule: any) => ({
          id: rule.id,
          name: rule.name,
        })),
      );
      setShowRule(false);
    } else if (ruleSelected) {
      setSelectedRule([ruleSelected]);
    }
  }, [itemCreated, ruleSelected]);

  return (
    <>
      <Mobile>
        <AppBar goBack={handleNavigateHistory}>
          <h5>{t('rule.ruleList.ruleListTitle')}</h5>
          <SubTitle>{groupTarget}</SubTitle>
        </AppBar>
      </Mobile>

      {isLoading ? (
        <Loader fullScreen={isMobile} />
      ) : (
        <>
          <Desktop>
            <ModalHeader title={t('userMenu.rulesTitle')} onClose={onCloseSelectionScreen} />
          </Desktop>

          <BoxContainer>
            <ButtonGhost onClick={() => setShowRule(true)}>
              <ButtonGhost.Icon src="/corporate/images/policy_create.svg" />
              {t('rule.ruleList.btnCreateRule')}
            </ButtonGhost>
          </BoxContainer>
          {renderAddRule()}
          <Search
            show={ruleList.length >= 10}
            onShowSearch={(isSearch: boolean) => setShowSearch(isSearch)}
            onSearch={handleOnSearch}
            fullWidth
          />
          {showSearch ? (
            <List>
              <GroupedItem
                lineGroup
                quantityText={searchResultsText}
                isSearch={showSearch}
                items={rulesResult}
                selectedItems={selectedRule}
                selectableType="radio"
                onSelection={handleSelection}
              />
            </List>
          ) : (
            <>
              <GroupedList
                isAlphabetic
                lineGroup
                isSelectable={false}
                items={ruleList}
                isSearch={showSearch}
                selectableType="radio"
                onSelection={handleSelection}
                currentItem={selectedRule}
              />
              {selectedRule.length > 0 && selectedRule[0].id && (
                <ButtonContainer>
                  <StyledButton onClick={handleSubmitSelection} value={t('rule.ruleList.btnSelection')} />
                </ButtonContainer>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default RuleSelection;
